import React, { useState } from 'react';
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  User, 
  ArrowUp,
  Edit3,
  MoreHorizontal
} from 'lucide-react';

const ActionButtons = ({ ticket, onUpdate, userRole, availableAgents = [] }) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);

  const statusOptions = [
    { value: 'open', label: 'Open', icon: Clock, color: 'text-blue-600 dark:text-blue-300' },
    { value: 'in_progress', label: 'In Progress', icon: Edit3, color: 'text-yellow-600 dark:text-yellow-300' },
    { value: 'resolved', label: 'Resolved', icon: CheckCircle, color: 'text-green-600 dark:text-green-300' },
    { value: 'closed', label: 'Closed', icon: XCircle, color: 'text-content-secondary' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600 dark:text-yellow-300' },
    { value: 'high', label: 'High', color: 'text-orange-600 dark:text-orange-300' },
    { value: 'critical', label: 'Critical', color: 'text-red-600 dark:text-red-300' }
  ];

  const handleStatusChange = (newStatus) => {
    const updatedTicket = {
      ...ticket,
      status: newStatus,
      updatedAt: new Date(),
      ...(newStatus === 'resolved' && !ticket.resolvedAt && { resolvedAt: new Date() })
    };
    
    onUpdate(updatedTicket);
    setShowStatusDropdown(false);
  };

  const handleAssignmentChange = (agentId, agentName) => {
    const updatedTicket = {
      ...ticket,
      assignedAgentId: agentId,
      assignedAgentName: agentName,
      updatedAt: new Date()
    };
    
    onUpdate(updatedTicket);
    setShowAssignDropdown(false);
  };

  const handlePriorityChange = (newPriority) => {
    const updatedTicket = {
      ...ticket,
      priority: newPriority,
      updatedAt: new Date()
    };
    
    onUpdate(updatedTicket);
  };

  const handleEscalate = () => {
    const updatedTicket = {
      ...ticket,
      escalationLevel: (ticket.escalationLevel || 0) + 1,
      updatedAt: new Date()
    };
    
    onUpdate(updatedTicket);
    setShowMoreActions(false);
  };

  // Only show action buttons for agents and managers
  if (userRole === 'customer') {
    return null;
  }

  return (
    <div className="p-6 border-t border-line bg-gray-50 dark:bg-dark-700">
      <h3 className="text-sm font-medium text-content mb-4">Ticket Actions</h3>
      
      <div className="flex flex-wrap gap-3">
        {/* Status Change Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            className="flex items-center gap-2 px-3 py-2 bg-surface border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
          >
            {statusOptions.find(s => s.value === ticket.status)?.icon && 
              React.createElement(statusOptions.find(s => s.value === ticket.status).icon, {
                className: `w-4 h-4 ${statusOptions.find(s => s.value === ticket.status).color}`
              })
            }
            <span className="text-sm">Change Status</span>
          </button>
          
          {showStatusDropdown && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-surface border border-line rounded-lg shadow-lg z-10">
              {statusOptions.map((status) => (
                <button
                  key={status.value}
                  onClick={() => handleStatusChange(status.value)}
                  disabled={status.value === ticket.status}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    status.value === ticket.status ? 'bg-gray-100 dark:bg-dark-700 cursor-not-allowed' : ''
                  }`}
                >
                  <status.icon className={`w-4 h-4 ${status.color}`} />
                  <span className="text-sm">{status.label}</span>
                  {status.value === ticket.status && (
                    <span className="ml-auto text-xs text-content-muted">Current</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Assignment Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowAssignDropdown(!showAssignDropdown)}
            className="flex items-center gap-2 px-3 py-2 bg-surface border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
          >
            <User className="w-4 h-4 text-content-secondary" />
            <span className="text-sm">
              {ticket.assignedAgentName ? `Assigned to ${ticket.assignedAgentName}` : 'Assign Agent'}
            </span>
          </button>
          
          {showAssignDropdown && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-surface border border-line rounded-lg shadow-lg z-10">
              <button
                onClick={() => handleAssignmentChange(null, null)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors rounded-t-lg"
              >
                <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-dark-600 flex items-center justify-center">
                  <span className="text-xs text-content-secondary">—</span>
                </div>
                <span className="text-sm">Unassigned</span>
              </button>
              
              {availableAgents.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => handleAssignmentChange(agent.id, agent.name)}
                  disabled={agent.id === ticket.assignedAgentId}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors last:rounded-b-lg ${
                    agent.id === ticket.assignedAgentId ? 'bg-gray-100 dark:bg-dark-700 cursor-not-allowed' : ''
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      {agent.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm">{agent.name}</span>
                  {agent.id === ticket.assignedAgentId && (
                    <span className="ml-auto text-xs text-content-muted">Current</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Priority Change Buttons */}
        <div className="flex items-center gap-1">
          {priorityOptions.map((priority) => (
            <button
              key={priority.value}
              onClick={() => handlePriorityChange(priority.value)}
              disabled={priority.value === ticket.priority}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                priority.value === ticket.priority
                  ? 'bg-gray-200 dark:bg-dark-600 text-content-secondary cursor-not-allowed'
                  : ' border border-line-strong hover:bg-gray-50 dark:hover:bg-dark-700'
              }`}
              title={`Set priority to ${priority.label}`}
            >
              {priority.label}
            </button>
          ))}
        </div>

        {/* More Actions Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowMoreActions(!showMoreActions)}
            className="flex items-center gap-2 px-3 py-2 bg-surface border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
          >
            <MoreHorizontal className="w-4 h-4 text-content-secondary" />
            <span className="text-sm">More</span>
          </button>
          
          {showMoreActions && (
            <div className="absolute top-full right-0 mt-1 w-48 bg-surface border border-line rounded-lg shadow-lg z-10">
              <button
                onClick={handleEscalate}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors rounded-t-lg"
              >
                <ArrowUp className="w-4 h-4 text-orange-600 dark:text-orange-300" />
                <span className="text-sm">Escalate Ticket</span>
              </button>
              
              <button
                onClick={() => {
                  // Future: Add merge functionality
                  setShowMoreActions(false);
                }}
                disabled
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-content-subtle cursor-not-allowed"
              >
                <Edit3 className="w-4 h-4" />
                <span className="text-sm">Merge Ticket</span>
                <span className="ml-auto text-xs">(Soon)</span>
              </button>
              
              <button
                onClick={() => {
                  // Future: Add archive functionality
                  setShowMoreActions(false);
                }}
                disabled
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-content-subtle cursor-not-allowed rounded-b-lg"
              >
                <XCircle className="w-4 h-4" />
                <span className="text-sm">Archive Ticket</span>
                <span className="ml-auto text-xs">(Soon)</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions for Common Status Changes */}
      {ticket.status === 'open' && (
        <div className="mt-4 pt-4 border-t border-line">
          <p className="text-xs text-content-secondary mb-2">Quick Actions:</p>
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusChange('in_progress')}
              className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs font-medium rounded-full hover:bg-yellow-200 dark:hover:bg-yellow-900/40 transition-colors"
            >
              Start Working
            </button>
            <button
              onClick={() => handleStatusChange('resolved')}
              className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors"
            >
              Mark Resolved
            </button>
          </div>
        </div>
      )}

      {ticket.status === 'in_progress' && (
        <div className="mt-4 pt-4 border-t border-line">
          <p className="text-xs text-content-secondary mb-2">Quick Actions:</p>
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusChange('resolved')}
              className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors"
            >
              Mark Resolved
            </button>
            <button
              onClick={() => handleStatusChange('open')}
              className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors"
            >
              Back to Open
            </button>
          </div>
        </div>
      )}

      {ticket.status === 'resolved' && (
        <div className="mt-4 pt-4 border-t border-line">
          <p className="text-xs text-content-secondary mb-2">Quick Actions:</p>
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusChange('closed')}
              className="px-3 py-1 bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200 text-xs font-medium rounded-full hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
            >
              Close Ticket
            </button>
            <button
              onClick={() => handleStatusChange('in_progress')}
              className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs font-medium rounded-full hover:bg-yellow-200 dark:hover:bg-yellow-900/40 transition-colors"
            >
              Reopen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionButtons;